/**
 * Marcador de caducidad de la lista de recorridos — «stale-while-revalidate».
 *
 * ## Por qué existe
 *
 * El loader del listado es **cache-read-first**: hace `client.readQuery` y solo
 * llama al servidor si la caché está vacía. Por eso, cuando una mutación cambia
 * el estado de un recorrido, ni `refetchQueries` (esa query nunca es un
 * observable) ni `revalidate()` (reejecuta el loader, que vuelve a leer su
 * caché) consiguen refrescar la tabla. Medido: volver a la lista tras una
 * mutación daba **0 peticiones**.
 *
 * ## Por qué NO se invalida la caché
 *
 * El primer intento fue `cache.evict({ fieldName: 'surveillanceRoutes' })`, el
 * patrón que usa `evictDerivedCaches` para las otras seis entidades. Funciona
 * con red, pero **borrar es destructivo**: medido en el navegador con la red
 * cortada, la lista pasaba de enseñar sus 26 filas a enseñar **0**. El operario
 * de un recorrido de vigilancia trabaja en carretera, donde la cobertura se cae
 * a menudo, y la secuencia «mutación con éxito → se cae la red → abrir la
 * lista» le dejaba una tabla vacía donde antes veía sus recorridos. Como la
 * lista es la puerta de entrada al detalle, además se quedaba sin poder navegar.
 *
 * Lo que hace falta no es «bórralo», es «esto ha caducado: vuelve a pedirlo la
 * próxima vez que puedas, y si no puedes sigue enseñando lo viejo».
 *
 * ## Por qué a mano y no con Apollo
 *
 * Apollo no trae ese comportamiento (verificado en su documentación y en las
 * discusiones del proyecto, 2026-09):
 *
 * - `cache.evict` **borra**: es justo lo que hay que evitar.
 * - `cache.modify` con el centinela `INVALIDATE` marca el campo sucio para que
 *   los **observables** se reevalúen, pero NO cambia el valor — un `readQuery`
 *   como el del loader lo sigue sirviendo igual, así que no sirve aquí. Su
 *   propia documentación recomienda combinarlo con `client.refetchQueries`.
 * - `client.refetchQueries` **por nombre** solo alcanza observables vivos, y
 *   esta query no lo es nunca. Por documento haría falta el juego EXACTO de
 *   variables (la caché está partida por todos los filtros vía `keyArgs`), y
 *   quien dispara la mutación —el cronómetro, un modal, el detalle— no las
 *   tiene: pedir con otras variables escribe en OTRA partición y deja la lista
 *   visible igual de rancia.
 *
 * No hay equivalente al `invalidateQueries` de TanStack Query, que marca sin
 * borrar. Así que se implementa aquí, en quince líneas.
 *
 * ## Cómo funciona
 *
 * Un contador global que sube cada vez que algo cambia el estado de un
 * recorrido, y un registro de en qué versión se quedó cada consumidor. El
 * loader pregunta si va por detrás; si va, pide al servidor.
 *
 * 🔴 El consumidor se marca al día **solo cuando la lectura llegó al servidor**.
 * No basta con que la promesa no lance: sin cobertura la app pone
 * `client.prioritizeCacheValues = true` y Apollo convierte el `network-only` en
 * `cache-first`, así que resuelve con lo cacheado y sin error. El loader
 * comprueba ese interruptor antes de marcar. Sin esa comprobación —medido— la
 * lista se daba por refrescada estando sin red y no se reintentaba al volver la
 * cobertura. Ese detalle es el que convierte esto en «stale-while-revalidate» de
 * verdad y no en un flag que se consume y se pierde.
 *
 * Vive en memoria a propósito: al recargar la app los loaders arrancan de cero
 * y la caché persistida se revalida por su cuenta, así que no hay nada que
 * conservar entre sesiones.
 */

/** Sube cada vez que una mutación cambia el estado de algún recorrido. */
let staleVersion = 0;

/** Última versión que cada consumidor consiguió traer del servidor. */
const fetchedVersionByConsumer = new Map<string, number>();

/**
 * Marca la lista de recorridos como caducada. La llaman las mutaciones que
 * mueven el estado de un recorrido (ver `useSurveillanceRoutes`).
 */
export function markSurveillanceRouteListStale(): void {
  staleVersion += 1;
}

/**
 * Versión de caducidad vigente. El loader la captura ANTES de pedir al servidor
 * para poder marcarse al día con ELLA y no con la de después (ver
 * `markSurveillanceRouteListFresh`).
 */
export function currentSurveillanceRouteListVersion(): number {
  return staleVersion;
}

/**
 * ¿Este consumidor sirve datos anteriores a la última caducidad? Los loaders lo
 * usan para decidir si deben ir al servidor aunque tengan la lista en caché.
 */
export function isSurveillanceRouteListStale(consumer: string): boolean {
  return (fetchedVersionByConsumer.get(consumer) ?? 0) < staleVersion;
}

/**
 * Marca al consumidor al día hasta la versión que traía cuando EMPEZÓ a pedir.
 *
 * 🔴 `version` es obligatoria y tiene que ser la capturada ANTES de la petición.
 * Si se marcara con la versión de después, una mutación que caducara la lista
 * MIENTRAS la petición estaba en vuelo quedaría tapada por el resultado de esa
 * petición —que no la incluye— y no se reintentaría. Con el cronómetro en marcha
 * y el operario navegando, esa carrera es alcanzable.
 */
export function markSurveillanceRouteListFresh(
  consumer: string,
  version: number,
): void {
  // 🔴 Nunca hacia atrás. Desde que el refresco de segundo plano suelta su
  // turno a los 25 s, una petición vieja puede resolver DESPUÉS de una nueva:
  // la vieja llegaría con una versión menor y volvería a marcar como caducado
  // algo que ya estaba al día, provocando un refetch que nadie pidió. Se queda
  // la más alta de las dos.
  const current = fetchedVersionByConsumer.get(consumer) ?? 0;
  fetchedVersionByConsumer.set(consumer, Math.max(current, version));
}

/** Solo para tests: devuelve el marcador a su estado inicial. */
export function resetSurveillanceRouteListFreshness(): void {
  staleVersion = 0;
  fetchedVersionByConsumer.clear();
}
